import type { CurvePoint } from '../../../shared/api/CurvesCacheService';
import type { LogCurve } from '../../../shared/api/IWellDirectoryService';
import { BaseParameterTypes, baseParamsToMnemonic } from '../../../shared/baseParams';
import type { UnitManager } from '../../../shared/services/unit-manager';
import { hasValue } from '../../../shared/utils';
import { HeatmapBinner } from '../ChartStore/HeatmapBinner';
import type { HeatmapBinningResult } from '../ChartStore/types';
import type { HeatmapWidgetSettingsStore } from '../SettingsStore/HeatmapWidgetSettingsStore';
import { OperationRegimes } from '../types';

type CurvesDataMap = Partial<Record<BaseParameterTypes, LogCurve>>;
type CurveCoordinatesMap = Partial<Record<BaseParameterTypes, CurvePoint[]>>;
export class HeatmapDataProcessor {
  private lastBinner: HeatmapBinner | null = null;
  private lastWindowDepth = -Infinity;

  constructor(private readonly unitManager: UnitManager) {}

  checkCanCreateHeatmap(parameters: BaseParameterTypes[], curvesData: CurvesDataMap | null): string[] | null {
    const missing: string[] = [];
    parameters.forEach((parameter) => {
      if (!curvesData?.[parameter]) {
        const parameterName = baseParamsToMnemonic[parameter][0];
        missing.push(parameterName);
      }
    });
    return missing.length > 0 ? missing : null;
  }

  buildHeatmapData(params: {
    settings: HeatmapWidgetSettingsStore;
    curvesData: CurvesDataMap | null;
    curveCoordinates: CurveCoordinatesMap | null;
  }): HeatmapBinningResult {
    this.clearLastBinner();
    const { settings, curvesData, curveCoordinates } = params;
    const emptyResult: HeatmapBinningResult = {
      data: [],
      ropRange: null,
      mseRange: null,
      activeRopBins: [],
      activeMseBins: [],
    };

    if (!this.validateRequiredCurves(settings, curvesData)) {
      return emptyResult;
    }

    const xParameter = settings.currentXAxisParameter;
    const yParameter = settings.currentYAxisParameter;
    const mseParameter = settings.currentMseAxisParameter;

    const xPoints = this.getCurveCoordinates(curveCoordinates, xParameter);
    const yPoints = this.getCurveCoordinates(curveCoordinates, yParameter);
    const rpmPoints = this.getCurveCoordinates(curveCoordinates, BaseParameterTypes.ROTARY_SPEED);
    const ropPoints = this.getCurveCoordinates(curveCoordinates, BaseParameterTypes.RATE_OF_PENETRATION);
    const msePoints = this.getCurveCoordinates(curveCoordinates, mseParameter);

    if (!this.validateCurveCoordinates(xPoints, yPoints, ropPoints, rpmPoints)) {
      return emptyResult;
    }

    if (!xPoints || !yPoints || !ropPoints || !rpmPoints) {
      return emptyResult;
    }

    const { startDepth, endDepth, depthUnit } = settings;
    const startDepthMeters =
      depthUnit === 'ft' ? Math.round(this.unitManager.convert('ft', 'm', startDepth) * 10) / 10 : startDepth;
    const endDepthMeters =
      endDepth === null
        ? Infinity
        : depthUnit === 'ft'
          ? Math.round(this.unitManager.convert('ft', 'm', endDepth) * 10) / 10
          : endDepth;
    const converters = this.prepareUnitConverters(settings, curvesData, xParameter, yParameter, msePoints);

    return this.synchronizeCurvesData(
      settings,
      xPoints,
      yPoints,
      ropPoints,
      rpmPoints,
      msePoints,
      startDepthMeters,
      endDepthMeters,
      converters,
    );
  }

  clearLastBinner() {
    this.lastBinner = null;
    this.lastWindowDepth = -Infinity;
  }

  recalculateActiveBins(settings: HeatmapWidgetSettingsStore) {
    return (
      this.lastBinner?.getActiveBins(this.lastWindowDepth - this.getWindowRangeMeters(settings)) ?? {
        activeRopBins: [],
        activeMseBins: [],
      }
    );
  }

  private validateRequiredCurves(settings: HeatmapWidgetSettingsStore, curvesData: CurvesDataMap | null): boolean {
    const xParameter = settings.currentXAxisParameter;
    const yParameter = settings.currentYAxisParameter;
    const ropParameter = BaseParameterTypes.RATE_OF_PENETRATION;
    const rpmParameter = BaseParameterTypes.ROTARY_SPEED;

    const xCurve = curvesData?.[xParameter];
    const yCurve = curvesData?.[yParameter];
    const ropCurve = curvesData?.[ropParameter];
    const rpmCurve = curvesData?.[rpmParameter];

    const hasAllRequired = !!(xCurve && yCurve && ropCurve && rpmCurve);

    if (!hasAllRequired) {
      const missing: BaseParameterTypes[] = [];
      if (!xCurve) {
        missing.push(xParameter);
      }
      if (!yCurve) {
        missing.push(yParameter);
      }
      if (!ropCurve) {
        missing.push(ropParameter);
      }
      if (!rpmCurve) {
        missing.push(rpmParameter);
      }

      console.warn('Not enough curves to build heatmap. Missing:', missing);
    }

    return hasAllRequired;
  }

  private validateCurveCoordinates(
    xPoints: CurvePoint[] | null,
    yPoints: CurvePoint[] | null,
    ropPoints: CurvePoint[] | null,
    rpmPoints: CurvePoint[] | null,
  ): boolean {
    const hasAllCoordinates = Boolean(xPoints?.length && yPoints?.length && ropPoints?.length && rpmPoints?.length);

    return hasAllCoordinates;
  }

  private prepareUnitConverters(
    settings: HeatmapWidgetSettingsStore,
    curvesData: CurvesDataMap | null,
    xParameter: BaseParameterTypes,
    yParameter: BaseParameterTypes,
    msePoints: CurvePoint[] | null,
  ) {
    const getUnit = (param: BaseParameterTypes) => curvesData?.[param]?.unit.abbreviation;

    const xCurveUnit = getUnit(xParameter);
    const yCurveUnit = getUnit(yParameter);
    const ropCurveUnit = getUnit(BaseParameterTypes.RATE_OF_PENETRATION);
    const mseParameter = settings.currentMseAxisParameter;
    const mseCurveUnit = msePoints ? getUnit(mseParameter) : undefined;

    const xAxisUnit = settings.currentXAxisParams.unit.abbreviation || xCurveUnit;
    const yAxisUnit = settings.currentYAxisParams.unit.abbreviation || yCurveUnit;
    const ropAxisUnit = settings.ropAxisParams.unit.abbreviation || ropCurveUnit;
    const mseAxisUnit = settings.currentMseAxisParams.unit.abbreviation || mseCurveUnit;

    return {
      convertX: this.createUnitConverter(xCurveUnit, xAxisUnit),
      convertY: this.createUnitConverter(yCurveUnit, yAxisUnit),
      convertRop: this.createUnitConverter(ropCurveUnit, ropAxisUnit),
      convertMse: this.createUnitConverter(mseCurveUnit, mseAxisUnit),
    };
  }

  private synchronizeCurvesData(
    settings: HeatmapWidgetSettingsStore,
    xPoints: CurvePoint[],
    yPoints: CurvePoint[],
    ropPoints: CurvePoint[],
    rpmPoints: CurvePoint[],
    msePoints: CurvePoint[] | null,
    startDepth: number,
    endDepth: number,
    converters: {
      convertX: (value: number) => number;
      convertY: (value: number) => number;
      convertRop: (value: number) => number;
      convertMse: (value: number) => number;
    },
  ): HeatmapBinningResult {
    const binner = new HeatmapBinner(settings.currentXAxisParams, settings.currentYAxisParams);
    const { rop: ropFilter, mse: mseFilter } = settings.currentZFilterParams;
    const ropMin = ropFilter.min;
    const ropMax = ropFilter.max;
    const mseMin = mseFilter.min;
    const mseMax = mseFilter.max;
    const findFirstIndexAtOrAfter = (points: CurvePoint[], startIndex: number, targetKey: number): number => {
      // Бинарный поиск от текущего курсора, чтобы избежать линейного пересканирования.
      if (startIndex >= points.length) {
        return points.length;
      }

      let left = startIndex;
      let right = points.length - 1;
      let result = points.length;

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (points[mid].key >= targetKey) {
          result = mid;
          right = mid - 1;
        } else {
          left = mid + 1;
        }
      }

      return result;
    };

    let xIndex = findFirstIndexAtOrAfter(xPoints, 0, startDepth);
    let yIndex = findFirstIndexAtOrAfter(yPoints, 0, startDepth);
    let ropIndex = findFirstIndexAtOrAfter(ropPoints, 0, startDepth);
    let rpmIndex = findFirstIndexAtOrAfter(rpmPoints, 0, startDepth);
    let mseIndex = msePoints ? findFirstIndexAtOrAfter(msePoints, 0, startDepth) : 0;

    while (
      xIndex < xPoints.length &&
      yIndex < yPoints.length &&
      ropIndex < ropPoints.length &&
      rpmIndex < rpmPoints.length
    ) {
      // Выравниваем все кривые по одной глубине и обрабатываем только синхронизированные точки.
      const xPoint = xPoints[xIndex];
      const yPoint = yPoints[yIndex];
      const ropPoint = ropPoints[ropIndex];
      const rpmPoint = rpmPoints[rpmIndex];

      const minKey = Math.min(xPoint.key, yPoint.key, ropPoint.key, rpmPoint.key);
      if (minKey > endDepth) {
        break;
      }

      const maxKey = Math.max(xPoint.key, yPoint.key, ropPoint.key, rpmPoint.key);

      if (xPoint.key < maxKey) {
        xIndex = findFirstIndexAtOrAfter(xPoints, xIndex + 1, maxKey);
        continue;
      }

      if (yPoint.key < maxKey) {
        yIndex = findFirstIndexAtOrAfter(yPoints, yIndex + 1, maxKey);
        continue;
      }

      if (ropPoint.key < maxKey) {
        ropIndex = findFirstIndexAtOrAfter(ropPoints, ropIndex + 1, maxKey);
        continue;
      }

      if (rpmPoint.key < maxKey) {
        rpmIndex = findFirstIndexAtOrAfter(rpmPoints, rpmIndex + 1, maxKey);
        continue;
      }

      if (!this.regimeRpmValidation(settings.operationRegimes, rpmPoint.value)) {
        xIndex += 1;
        yIndex += 1;
        ropIndex += 1;
        rpmIndex += 1;
        continue;
      }

      let mseValue: number | null = null;
      if (msePoints && mseIndex < msePoints.length) {
        if (msePoints[mseIndex].key < maxKey) {
          mseIndex = findFirstIndexAtOrAfter(msePoints, mseIndex + 1, maxKey);
        }
        const msePoint = msePoints[mseIndex];
        if (msePoint?.key === maxKey && Number.isFinite(msePoint.value)) {
          mseValue = converters.convertMse(msePoint.value);
          mseIndex += 1;
        }
      }

      const ropValue = converters.convertRop(ropPoint.value);
      const hasRopValue =
        Number.isFinite(ropValue) && (ropMin === null || ropValue >= ropMin) && (ropMax === null || ropValue <= ropMax);

      const hasMseValue =
        hasValue(mseValue) &&
        Number.isFinite(mseValue) &&
        (mseMin === null || mseValue >= mseMin) &&
        (mseMax === null || mseValue <= mseMax);

      if (!hasRopValue && !hasMseValue) {
        xIndex += 1;
        yIndex += 1;
        ropIndex += 1;
        rpmIndex += 1;
        continue;
      }

      const xValue = converters.convertX(xPoint.value);
      const yValue = converters.convertY(yPoint.value);
      // Preserve the window anchor: the last synchronized point passing either Z filter.
      this.lastWindowDepth = maxKey;
      binner.addPoint(xValue, yValue, hasRopValue ? ropValue : null, hasMseValue ? mseValue : null, maxKey);

      xIndex += 1;
      yIndex += 1;
      ropIndex += 1;
      rpmIndex += 1;
    }

    this.lastBinner = binner;
    const result = binner.toResult({
      ropAnalysisType: settings.ropAxisParams.analysisType,
      mseAnalysisType: settings.currentMseAxisParams.analysisType,
    });
    return {
      ...result,
      ...this.recalculateActiveBins(settings),
    };
  }

  private getWindowRangeMeters(settings: HeatmapWidgetSettingsStore): number {
    if (settings.depthUnit === 'm') {
      return settings.lastBinsRange;
    }
    return this.unitManager.convert('ft', 'm', settings.lastBinsRange);
  }

  private regimeRpmValidation(operationRegimes: OperationRegimes, value?: number) {
    if (!hasValue(value)) {
      return false;
    }
    if (operationRegimes === OperationRegimes.ROTATION) {
      return value >= 10;
    }
    return value < 10;
  }

  private createUnitConverter(sourceUnit?: string, targetUnit?: string) {
    if (!sourceUnit || !targetUnit || sourceUnit === targetUnit || !sourceUnit.trim() || !targetUnit.trim()) {
      return (value: number) => value;
    }

    return (value: number) => {
      try {
        return this.unitManager.convert(sourceUnit, targetUnit, value);
      } catch (error) {
        console.warn('Failed to convert heatmap value', {
          error,
          sourceUnit,
          targetUnit,
        });
        return value;
      }
    };
  }

  private getCurveCoordinates(curveCoordinates: CurveCoordinatesMap | null, key: BaseParameterTypes) {
    return curveCoordinates?.[key] || null;
  }
}
